import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { 
  HelpCircle, MessageCircle, Send, ChevronDown, ChevronRight, 
  ArrowLeft, Bot, User, Mail, AlertCircle
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const HelpCenter = ({ user }) => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('faq');
  const [expandedFaq, setExpandedFaq] = useState(null);
  
  // Chat state
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [usage, setUsage] = useState({ used: 0, remaining: 30, limit: 30 });
  const messagesEndRef = useRef(null);

  const faqData = [
    {
      category: "Διαχείριση Χρηστών & Ομάδας",
      items: [
        {
          q: "Πώς δημιουργώ λογαριασμό υπαλλήλου;",
          a: `Βήματα:
1. Συνδεθείτε με τον λογαριασμό σας (ως ιδιοκτήτης)
2. Πατήστε το κουμπί "Ομάδα" στο επάνω μέρος της οθόνης
3. Πατήστε "Προσθήκη Μέλους"
4. Συμπληρώστε το email, τον κωδικό και το ονοματεπώνυμο του υπαλλήλου
5. Πατήστε "Δημιουργία"

Ο υπάλληλος θα μπορεί να συνδεθεί με τα στοιχεία που του δώσατε και θα βλέπει μόνο τα έργα που του έχουν ανατεθεί.`
        }
      ]
    },
    {
      category: "Ειδοποιήσεις Αιτήσεων",
      items: [
        {
          q: "Πώς ενεργοποιώ τις αυτόματες ειδοποιήσεις αίτησης;",
          a: `Βήματα:
1. Ανοίξτε ένα έργο "Αίτησης Επενδυτικού Σχεδίου"
2. Πατήστε το εικονίδιο με το καμπανάκι (🔔) δίπλα στον τίτλο
3. Ενεργοποιήστε τις "Αυτόματες Υπενθυμίσεις"
4. Ορίστε κάθε πόσες ημέρες να στέλνεται υπενθύμιση (π.χ. κάθε 3 ημέρες)
5. Βεβαιωθείτε ότι έχετε εισάγει email πελάτη στο έργο

Οι ειδοποιήσεις θα αποστέλλονται αυτόματα αν υπάρχουν εκκρεμή δικαιολογητικά.`
        }
      ]
    },
    {
      category: "Ειδοποιήσεις Υλοποίησης",
      items: [
        {
          q: "Πώς δημιουργώ τις αυτόματες ειδοποιήσεις υλοποίησης;",
          a: `Βήματα:
1. Ανοίξτε ένα έργο "Υλοποίησης Επενδυτικού Σχεδίου"
2. Πατήστε το εικονίδιο με το καμπανάκι (🔔) στο επάνω μέρος
3. Ενεργοποιήστε τις "Αυτόματες Υπενθυμίσεις"
4. Επιλέξτε το διάστημα αποστολής (π.χ. κάθε 3 ημέρες)

Οι ειδοποιήσεις στέλνονται στους συνεργάτες (προμηθευτές, λογιστές) που έχετε συνδέσει με κάθε δικαιολογητικό.`
        }
      ]
    },
    {
      category: "Συνεργάτες & Δικαιολογητικά",
      items: [
        {
          q: "Πώς συνδέω στα δικαιολογητικά προμηθευτές, πελάτες και λογιστές;",
          a: `Βήματα:
1. Ανοίξτε ένα έργο (Αίτησης ή Υλοποίησης)
2. Βρείτε το δικαιολογητικό που θέλετε να αναθέσετε
3. Πατήστε το κουμπί "Ανάθεση" (εικονίδιο χρήστη)
4. Επιλέξτε τον ρόλο: Πελάτης, Λογιστής, Προμηθευτής ή Άλλο
5. Εισάγετε το email του συνεργάτη
6. Πατήστε "Αποθήκευση"

Ο συνεργάτης θα λαμβάνει ειδοποιήσεις και θα μπορεί να ανεβάσει αρχεία μέσω του link που του στέλνεται.`
        }
      ]
    },
    {
      category: "CRM - Πελατολόγιο",
      items: [
        {
          q: "Πώς καταχωρώ νέο πελάτη;",
          a: `Βήματα:
1. Πηγαίνετε στο "Πελατολόγιο CRM" από την αρχική οθόνη
2. Πατήστε "Νέος Πελάτης"
3. Επιλέξτε τύπο: "Υφιστάμενη Επιχείρηση" ή "Υπό Σύσταση"
4. Συμπληρώστε τα απαιτούμενα στοιχεία:
   - Για Υφιστάμενη: ΑΦΜ, Επωνυμία, Κατηγορία, Περιφέρεια
   - Για Υπό Σύσταση: Όνομα, Κατηγορία Επιχείρησης, ΚΑΔ
5. Προαιρετικά: Προσθέστε email, τηλέφωνο, διεύθυνση
6. Πατήστε "Αποθήκευση"`
        },
        {
          q: "Που αναζητώ τη λίστα πελατών;",
          a: `Η λίστα πελατών βρίσκεται στο "Πελατολόγιο CRM":
1. Από την αρχική οθόνη, πατήστε την κάρτα "Πελατολόγιο CRM"
2. Θα δείτε τη λίστα όλων των πελατών σας
3. Χρησιμοποιήστε τα φίλτρα για αναζήτηση:
   - Κατά κατάσταση (Ενεργός, Ανενεργός, Όλοι)
   - Κατά ΑΦΜ ή Επωνυμία
   - Κατά ΚΑΔ
   - Κατά Πρόγραμμα ενδιαφέροντος
4. Πατήστε πάνω σε έναν πελάτη για να δείτε την πλήρη καρτέλα του`
        }
      ]
    },
    {
      category: "Ραντεβού",
      items: [
        {
          q: "Πώς δημιουργώ ένα ραντεβού;",
          a: `Υπάρχουν 2 τρόποι:

**Τρόπος 1 - Από καρτέλα πελάτη:**
1. Ανοίξτε την καρτέλα του πελάτη στο CRM
2. Πηγαίνετε στην καρτέλα "Επικοινωνίες"
3. Πατήστε "Νέα Επικοινωνία"
4. Συμπληρώστε το πεδίο "Επόμενη Επικοινωνία" με ημερομηνία
5. Αποθηκεύστε - θα δημιουργηθεί αυτόματα ραντεβού

**Τρόπος 2 - Γρήγορο Ραντεβού:**
1. Στο "Πελατολόγιο CRM", πατήστε τις 3 τελείες δίπλα σε έναν πελάτη
2. Επιλέξτε "Γρήγορο Ραντεβού"
3. Επιλέξτε ημερομηνία και ώρα
4. Πατήστε "Δημιουργία"`
        },
        {
          q: "Που βλέπω τα ραντεβού;",
          a: `Βήματα:
1. Πηγαίνετε στο "Πελατολόγιο CRM"
2. Πατήστε "Τα Ραντεβού μου" στο sidebar αριστερά
3. Εδώ βλέπετε όλα τα ραντεβού σας ταξινομημένα κατά ημερομηνία
4. Μπορείτε να φιλτράρετε:
   - Όλα τα Ραντεβού
   - Εκκρεμή
   - Ολοκληρωμένα
   - Με φίλτρο ημερομηνίας (Από - Έως)
5. Για κάθε ραντεβού μπορείτε να:
   - Δείτε Ιστορικό
   - Αναβάλετε
   - Ολοκληρώσετε Πώληση
   - Σημειώσετε ως "Δεν Ενδιαφέρεται"`
        }
      ]
    },
    {
      category: "Καθήκοντα & Επικοινωνίες Ομάδας",
      items: [
        {
          q: "Πώς δημιουργώ μια επικοινωνία/καθήκον με την ομάδα μου;",
          a: `Βήματα:
1. Πηγαίνετε στο "Πελατολόγιο CRM"
2. Πατήστε "Καθήκοντα & Επικοινωνίες" στο sidebar
3. Πατήστε "Νέο Καθήκον"
4. Συμπληρώστε:
   - Τίτλο και περιγραφή
   - Επιλέξτε πελάτη (προαιρετικά)
   - Αναθέστε σε μέλος της ομάδας
5. Πατήστε "Δημιουργία"

Το μέλος της ομάδας θα βλέπει το καθήκον και μπορεί να απαντήσει με σχόλια ή συνημμένα.`
        },
        {
          q: "Που βλέπω τις επικοινωνίες με την ομάδα;",
          a: `Βήματα:
1. Πηγαίνετε στο "Πελατολόγιο CRM"
2. Πατήστε "Καθήκοντα & Επικοινωνίες" στο sidebar
3. Εδώ βλέπετε όλα τα καθήκοντα:
   - Εκκρεμή (πορτοκαλί badge)
   - Ολοκληρωμένα (πράσινο badge)
4. Φιλτράρετε:
   - Κατά κατάσταση
   - Κατά ανατεθειμένο
   - Κατά ημερομηνία
5. Πατήστε σε ένα καθήκον για να δείτε τις απαντήσεις και να προσθέσετε νέες`
        }
      ]
    },
    {
      category: "Έλεγχος Δικαιολογητικών",
      items: [
        {
          q: "Πώς κάνω αυτόματο έλεγχο δικαιολογητικών αίτησης;",
          a: `Βήματα:
1. Ανοίξτε ένα έργο "Αίτησης Επενδυτικού Σχεδίου"
2. Βρείτε το δικαιολογητικό που θέλετε να ελέγξετε
3. Βεβαιωθείτε ότι έχει ανεβεί αρχείο (π.χ. PDF)
4. Πατήστε το κουμπί "Έλεγχος" (εικονίδιο με σημάδι ελέγχου)
5. Περιμένετε λίγα δευτερόλεπτα
6. Θα εμφανιστεί το αποτέλεσμα:
   - ✅ ΕΠΙΤΥΧΙΑ: Το έγγραφο είναι έγκυρο
   - ❌ ΑΠΟΤΥΧΙΑ: Με επεξήγηση του προβλήματος

Σημείωση: Κάθε έργο έχει μέχρι 3 ελέγχους διαθέσιμους.`
        }
      ]
    },
    {
      category: "Ρυθμίσεις Διαχειριστή",
      items: [
        {
          q: "Που προσθέτω τα νέα προγράμματα, τις κατηγορίες επιχειρήσεων και τις περιφέρειες;",
          a: `Βήματα:
1. Πηγαίνετε στο "Πελατολόγιο CRM"
2. Πατήστε "Ρυθμίσεις" στο sidebar (μόνο για ιδιοκτήτες)
3. Εδώ διαχειρίζεστε:
   
**Προγράμματα:**
   - Πατήστε "Προσθήκη Προγράμματος"
   - Εισάγετε όνομα και περιγραφή
   
**Κατηγορίες Επιχειρήσεων:**
   - Πατήστε "Προσθήκη Κατηγορίας"
   - Εισάγετε το όνομα
   
**Περιφέρειες:**
   - Πατήστε "Προσθήκη Περιφέρειας"
   - Εισάγετε το όνομα

Όλες οι επιλογές θα εμφανίζονται αυτόματα στις φόρμες δημιουργίας πελατών.`
        }
      ]
    },
    {
      category: "Πρότυπα Έργων",
      items: [
        {
          q: "Πώς αλλάζω μαζικά τις προθεσμίες στα έργα αιτήσεων και υλοποίησης από τα πρότυπα;",
          a: `Βήματα για Πρότυπα Αιτήσεων:
1. Από την αρχική, πατήστε "Πρότυπα Αιτήσεων"
2. Βρείτε το πρότυπο που θέλετε
3. Πατήστε "Επεξεργασία"
4. Αλλάξτε την "Προθεσμία"
5. Ενεργοποιήστε "Ενημέρωση συνδεδεμένων έργων"
6. Πατήστε "Αποθήκευση"

**Για Πρότυπα Υλοποίησης:** Ακολουθήστε τα ίδια βήματα από "Πρότυπα Υλοποίησης"

Όλα τα έργα που χρησιμοποιούν αυτό το πρότυπο θα ενημερωθούν με τη νέα προθεσμία.`
        },
        {
          q: "Πώς λειτουργούν τα πρότυπα αιτήσεων και πρότυπα υλοποίησης;",
          a: `**Τι είναι τα Πρότυπα:**
Τα πρότυπα είναι έτοιμες δομές με προκαθορισμένα δικαιολογητικά που μπορείτε να επαναχρησιμοποιήσετε.

**Πρότυπα Αιτήσεων:**
- Περιέχουν λίστα δικαιολογητικών για αιτήσεις ΕΣΠΑ
- Μπορείτε να ορίσετε προεπιλεγμένη προθεσμία
- Όταν δημιουργείτε νέο έργο, επιλέγετε πρότυπο και τα δικαιολογητικά προστίθενται αυτόματα

**Πρότυπα Υλοποίησης:**
- Περιέχουν δικαιολογητικά ανά κατηγορία δαπάνης
- Κατηγορίες: Εξοπλισμός, Λογισμικό, Κτίρια, Οχήματα κ.λπ.
- Κάθε κατηγορία έχει τα δικά της απαιτούμενα έγγραφα

**Δημιουργία Προτύπου:**
1. Πηγαίνετε σε "Πρότυπα Αιτήσεων" ή "Πρότυπα Υλοποίησης"
2. Πατήστε "Νέο Πρότυπο"
3. Δώστε όνομα και προσθέστε δικαιολογητικά`
        }
      ]
    },
    {
      category: "Υλοποίηση - Προϋπολογισμός",
      items: [
        {
          q: "Που προσθέτω τον προϋπολογισμό έργου στην υλοποίηση;",
          a: `Βήματα:
1. Ανοίξτε ένα έργο "Υλοποίησης Επενδυτικού Σχεδίου"
2. Στο επάνω μέρος της σελίδας, κάτω από τον τίτλο, θα δείτε το πεδίο "Προϋπολογισμός"
3. Πατήστε το εικονίδιο επεξεργασίας (μολύβι)
4. Εισάγετε:
   - Καθαρό ποσό (χωρίς ΦΠΑ)
   - Ποσό ΦΠΑ
5. Πατήστε "Αποθήκευση"

Ο συνολικός προϋπολογισμός θα εμφανίζεται στην καρτέλα του έργου.`
        },
        {
          q: "Πώς προσθέτω κατηγορίες δαπανών;",
          a: `Υπάρχουν 2 επίπεδα διαχείρισης:

**Επίπεδο 1 - Γενικές Κατηγορίες (για όλα τα έργα):**
1. Από την αρχική οθόνη, πατήστε το μενού "Ομάδα" και μετά "Κατηγορίες Δαπανών"
2. Πατήστε "Προσθήκη Κατηγορίας"
3. Εισάγετε όνομα (π.χ. "Εξοπλισμός") και τα δικαιολογητικά που απαιτούνται

**Επίπεδο 2 - Σε συγκεκριμένο έργο:**
1. Ανοίξτε ένα έργο Υλοποίησης
2. Πατήστε "Προσθήκη Τιμολογίου"
3. Επιλέξτε την κατηγορία δαπάνης από τη λίστα
4. Τα δικαιολογητικά της κατηγορίας θα προστεθούν αυτόματα

Οι κατηγορίες που δημιουργείτε θα είναι διαθέσιμες σε όλα τα νέα έργα.`
        }
      ]
    }
  ];

  useEffect(() => {
    fetchUsage();
    fetchHistory();
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const fetchUsage = async () => {
    try {
      const response = await axios.get(`${API}/support/chat/usage?user_id=${user.user_id}`);
      setUsage(response.data);
    } catch (error) {
      console.error('Error fetching usage:', error);
    }
  };

  const fetchHistory = async () => {
    try {
      const response = await axios.get(`${API}/support/chat/history?user_id=${user.user_id}&limit=20`);
      const formattedMessages = [];
      response.data.forEach(item => {
        formattedMessages.push({ role: 'user', content: item.user_message });
        formattedMessages.push({ role: 'assistant', content: item.ai_response });
      });
      setMessages(formattedMessages);
    } catch (error) {
      console.error('Error fetching history:', error);
    }
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!inputMessage.trim() || loading) return;

    const userMessage = inputMessage.trim();
    setInputMessage('');
    setMessages(prev => [...prev, { role: 'user', content: userMessage }]);
    setLoading(true);

    try {
      const response = await axios.post(`${API}/support/chat?user_id=${user.user_id}`, {
        message: userMessage
      });

      setMessages(prev => [...prev, { role: 'assistant', content: response.data.response }]);
      setUsage(prev => ({
        ...prev,
        used: prev.used + 1,
        remaining: response.data.remaining
      }));

      if (response.data.limit_reached) {
        toast.warning('Φτάσατε το ημερήσιο όριο ερωτήσεων');
      }
    } catch (error) {
      toast.error('Σφάλμα επικοινωνίας');
      setMessages(prev => prev.slice(0, -1)); // Remove user message on error
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
      {/* Header */}
      <div className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-5xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate('/select-module')}
                className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
                data-testid="back-btn"
              >
                <ArrowLeft className="h-5 w-5 text-slate-600" />
              </button>
              <div>
                <h1 className="text-xl font-bold text-slate-800 flex items-center gap-2">
                  <HelpCircle className="h-6 w-6 text-blue-600" />
                  Κέντρο Βοήθειας
                </h1>
                <p className="text-sm text-slate-500">Οδηγίες χρήσης & Υποστήριξη</p>
              </div>
            </div>
            
            {/* Tab Switcher */}
            <div className="flex bg-slate-100 rounded-lg p-1">
              <button
                onClick={() => setActiveTab('faq')}
                data-testid="faq-tab"
                className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                  activeTab === 'faq' 
                    ? 'bg-white text-slate-900 shadow-sm' 
                    : 'text-slate-600 hover:text-slate-900'
                }`}
              >
                Συχνές Ερωτήσεις
              </button>
              <button
                onClick={() => setActiveTab('chat')}
                data-testid="chat-tab"
                className={`px-4 py-2 rounded-md text-sm font-medium transition-colors flex items-center gap-2 ${
                  activeTab === 'chat' 
                    ? 'bg-white text-slate-900 shadow-sm' 
                    : 'text-slate-600 hover:text-slate-900'
                }`}
              >
                <Bot className="h-4 w-4" />
                AI Βοηθός
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 py-6">
        {/* FAQ Tab */}
        {activeTab === 'faq' && (
          <div className="space-y-6" data-testid="faq-content">
            {faqData.map((category, catIndex) => (
              <div key={catIndex} className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
                <div className="bg-gradient-to-r from-blue-50 to-indigo-50 px-6 py-4 border-b border-slate-100">
                  <h2 className="font-semibold text-slate-800">{category.category}</h2>
                </div>
                <div className="divide-y divide-slate-100">
                  {category.items.map((item, itemIndex) => {
                    const faqKey = `${catIndex}-${itemIndex}`;
                    const isExpanded = expandedFaq === faqKey;
                    return (
                      <div key={itemIndex}>
                        <button
                          onClick={() => setExpandedFaq(isExpanded ? null : faqKey)}
                          className="w-full px-6 py-4 text-left flex items-center justify-between hover:bg-slate-50 transition-colors"
                          data-testid={`faq-item-${catIndex}-${itemIndex}`}
                        >
                          <span className="font-medium text-slate-700">{item.q}</span>
                          {isExpanded ? (
                            <ChevronDown className="h-5 w-5 text-slate-400 flex-shrink-0" />
                          ) : (
                            <ChevronRight className="h-5 w-5 text-slate-400 flex-shrink-0" />
                          )}
                        </button>
                        {isExpanded && (
                          <div className="px-6 pb-4 text-slate-600 bg-slate-50 whitespace-pre-line">
                            {item.a}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
            
            {/* Contact Support */}
            <div className="bg-gradient-to-r from-blue-600 to-indigo-600 rounded-xl p-6 text-white">
              <div className="flex items-center gap-4">
                <div className="bg-white/20 p-3 rounded-lg">
                  <Mail className="h-6 w-6" />
                </div>
                <div>
                  <h3 className="font-semibold text-lg">Χρειάζεστε περισσότερη βοήθεια;</h3>
                  <p className="text-blue-100">Επικοινωνήστε μαζί μας στο</p>
                  <a 
                    href="mailto:espamanagerapp@gmail.com" 
                    className="text-white font-medium hover:underline"
                  >
                    espamanagerapp@gmail.com
                  </a>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Chat Tab */}
        {activeTab === 'chat' && (
          <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden" data-testid="chat-content">
            {/* Chat Header */}
            <div className="bg-gradient-to-r from-purple-50 to-blue-50 px-6 py-4 border-b border-slate-100">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="bg-purple-100 p-2 rounded-lg">
                    <Bot className="h-5 w-5 text-purple-600" />
                  </div>
                  <div>
                    <h2 className="font-semibold text-slate-800">AI Βοηθός Υποστήριξης</h2>
                    <p className="text-sm text-slate-500">Ρωτήστε οτιδήποτε για τη χρήση της εφαρμογής</p>
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-sm text-slate-600">
                    Ερωτήσεις σήμερα: <span className="font-semibold">{usage.used}/{usage.limit}</span>
                  </div>
                  <div className="text-xs text-slate-500">
                    Απομένουν: {usage.remaining}
                  </div>
                </div>
              </div>
            </div>

            {/* Messages */}
            <div className="h-[400px] overflow-y-auto p-4 space-y-4 bg-slate-50" data-testid="chat-messages">
              {messages.length === 0 && (
                <div className="text-center py-12">
                  <Bot className="h-12 w-12 text-slate-300 mx-auto mb-4" />
                  <p className="text-slate-500">Γεια σας! Πώς μπορώ να σας βοηθήσω;</p>
                  <p className="text-sm text-slate-400 mt-2">Ρωτήστε για οποιαδήποτε λειτουργία της εφαρμογής</p>
                </div>
              )}
              
              {messages.map((msg, index) => (
                <div
                  key={index}
                  className={`flex items-start gap-3 ${
                    msg.role === 'user' ? 'flex-row-reverse' : ''
                  }`}
                >
                  <div className={`p-2 rounded-lg ${
                    msg.role === 'user' ? 'bg-blue-100' : 'bg-purple-100'
                  }`}>
                    {msg.role === 'user' ? (
                      <User className="h-4 w-4 text-blue-600" />
                    ) : (
                      <Bot className="h-4 w-4 text-purple-600" />
                    )}
                  </div>
                  <div className={`max-w-[75%] p-3 rounded-lg ${
                    msg.role === 'user' 
                      ? 'bg-blue-600 text-white rounded-tr-none' 
                      : 'bg-white text-slate-700 rounded-tl-none shadow-sm'
                  }`}>
                    <p className="whitespace-pre-wrap">{msg.content}</p>
                  </div>
                </div>
              ))}
              
              {loading && (
                <div className="flex items-start gap-3">
                  <div className="bg-purple-100 p-2 rounded-lg">
                    <Bot className="h-4 w-4 text-purple-600" />
                  </div>
                  <div className="bg-white p-3 rounded-lg rounded-tl-none shadow-sm">
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 bg-purple-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                      <div className="w-2 h-2 bg-purple-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                      <div className="w-2 h-2 bg-purple-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                    </div>
                  </div>
                </div>
              )}
              
              <div ref={messagesEndRef} />
            </div>

            {/* Input */}
            <div className="p-4 border-t border-slate-200 bg-white">
              {usage.remaining === 0 ? (
                <div className="flex items-center gap-3 p-4 bg-amber-50 rounded-lg text-amber-800">
                  <AlertCircle className="h-5 w-5" />
                  <div>
                    <p className="font-medium">Ημερήσιο όριο ερωτήσεων</p>
                    <p className="text-sm">Επικοινωνήστε στο <a href="mailto:espamanagerapp@gmail.com" className="underline">espamanagerapp@gmail.com</a></p>
                  </div>
                </div>
              ) : (
                <form onSubmit={handleSendMessage} className="flex gap-2">
                  <Input
                    value={inputMessage}
                    onChange={(e) => setInputMessage(e.target.value)}
                    placeholder="Γράψτε την ερώτησή σας..."
                    disabled={loading}
                    className="flex-1"
                    data-testid="chat-input"
                  />
                  <Button 
                    type="submit" 
                    disabled={loading || !inputMessage.trim()}
                    data-testid="send-message-btn"
                  >
                    <Send className="h-4 w-4" />
                  </Button>
                </form>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default HelpCenter;
